import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Flame, AlertTriangle, Clock, Leaf,
  CheckCircle2, PauseCircle, Eye, AlertCircle, Trash2, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PRIORITIES = [
  {
    id: "critical", label: "KRİTİK / ACİL", short: "KRİTİK", icon: Flame,
    badge: "bg-red-600 text-white",
    badgePulse: "animate-pulse",
    border: "border-l-[6px] border-red-600",
    card: "bg-red-50/60",
    ring: "ring-2 ring-red-500/40",
    dot: "bg-red-500",
    hint: "Araç yattı / hat aksadı / anında müdahale",
  },
  {
    id: "high", label: "YÜKSEK", short: "YÜKSEK", icon: AlertTriangle,
    badge: "bg-orange-500 text-white",
    border: "border-l-[6px] border-orange-500",
    card: "bg-orange-50/60",
    ring: "",
    dot: "bg-orange-500",
    hint: "Günün ilk yarısı çözülmeli / acil parça",
  },
  {
    id: "medium", label: "ORTA", short: "ORTA", icon: Clock,
    badge: "bg-amber-400 text-slate-900",
    border: "border-l-[6px] border-amber-400",
    card: "bg-amber-50/50",
    ring: "",
    dot: "bg-amber-400",
    hint: "Gün içi / yarına kadar",
  },
  {
    id: "low", label: "NORMAL / DÜŞÜK", short: "NORMAL", icon: Leaf,
    badge: "bg-emerald-500 text-white",
    border: "border-l-[6px] border-emerald-500",
    card: "bg-emerald-50/50",
    ring: "",
    dot: "bg-emerald-500",
    hint: "Rutin eksik / genel not",
  },
];
const P_META = Object.fromEntries(PRIORITIES.map((p) => [p.id, p]));

const STATUSES = [
  { id: "open",       label: "Açık / Acil",  icon: AlertCircle, chip: "bg-red-100 text-red-800 border-red-300",           btn: "bg-red-50 hover:bg-red-100 text-red-700 border-red-300" },
  { id: "pending",    label: "Beklemede",    icon: PauseCircle, chip: "bg-amber-100 text-amber-800 border-amber-300",     btn: "bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-300" },
  { id: "reviewing",  label: "İnceleniyor",  icon: Eye,         chip: "bg-blue-100 text-blue-800 border-blue-300",        btn: "bg-blue-50 hover:bg-blue-100 text-blue-800 border-blue-300" },
  { id: "resolved",   label: "Çözüldü",      icon: CheckCircle2, chip: "bg-emerald-100 text-emerald-800 border-emerald-300", btn: "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300" },
];
const S_META = Object.fromEntries(STATUSES.map((s) => [s.id, s]));

const FILTERS = [
  { id: "all",     label: "Tümü" },
  { id: "urgent",  label: "🚨 Sadece Aciller" },
  { id: "pending", label: "Bekleyenler" },
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
};

export default function OperationalNotesWidget() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ urgent_open: 0, open_total: 0, total: 0, by_priority: {}, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState("medium");
  const [submitting, setSubmitting] = useState(false);

  const [busyIds, setBusyIds] = useState(() => new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/op-notes", { params: { limit: 200 } }),
        api.get("/op-notes/summary"),
      ]);
      setItems(list.items || []);
      setSummary(sum || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Notlar yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const filtered = useMemo(() => {
    if (filter === "urgent")  return items.filter((n) => (n.priority === "critical" || n.priority === "high") && n.status !== "resolved");
    if (filter === "pending") return items.filter((n) => n.status !== "resolved");
    return items;
  }, [items, filter]);

  const submit = async () => {
    const c = content.trim();
    if (!c) { toast.error("Not içeriği boş olamaz"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("content", c);
      fd.append("priority", priority);
      await api.post("/op-notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Not eklendi");
      setContent(""); setPriority("medium"); setShowForm(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally { setSubmitting(false); }
  };

  const markBusy = (id, on) => setBusyIds((prev) => {
    const s = new Set(prev);
    if (on) s.add(id); else s.delete(id);
    return s;
  });

  const changeStatus = async (id, status) => {
    if (!isAdmin) { toast.error("Durum değişikliği için yönetici yetkisi gerekli"); return; }
    markBusy(id, true);
    try {
      await api.patch(`/op-notes/${id}`, { status });
      toast.success(`Durum: ${S_META[status]?.label || status}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi");
    } finally { markBusy(id, false); }
  };

  const changePriority = async (id, p) => {
    markBusy(id, true);
    try {
      await api.patch(`/op-notes/${id}`, { priority: p });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi");
    } finally { markBusy(id, false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu notu kalıcı olarak silmek istediğinize emin misiniz?")) return;
    markBusy(id, true);
    try {
      await api.delete(`/op-notes/${id}`);
      toast.success("Silindi");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    } finally { markBusy(id, false); }
  };

  return (
    <div
      className="border border-slate-200 bg-white rounded-lg overflow-hidden shadow-sm"
      data-testid="op-notes-widget"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] text-white flex items-center justify-center">
            <ClipboardList className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Saha Operasyon</div>
            <div className="font-heading font-bold text-slate-900 text-lg">Notlar & İhtiyaçlar</div>
          </div>
          {summary?.urgent_open > 0 && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
              data-testid="op-notes-urgent-badge"
            >
              <Flame className="w-3 h-3" /> {summary.urgent_open} acil
            </span>
          )}
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="bg-[#1E3A8A] hover:bg-[#152C6C] text-white h-9"
          data-testid="op-notes-toggle-form"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {showForm ? "İptal" : "Yeni Not / İhtiyaç"}
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60" data-testid="op-notes-form">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Aciliyet Seçimi *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {PRIORITIES.map((p) => {
              const active = priority === p.id;
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPriority(p.id)}
                  data-testid={`op-notes-priority-${p.id}`}
                  className={`text-left px-3 py-2.5 rounded-md border transition ${
                    active
                      ? `${p.badge} border-transparent shadow-md ${p.id === "critical" ? "animate-pulse" : ""}`
                      : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <div className="flex items-center gap-2 text-[13px] font-bold">
                    <Icon className="w-3.5 h-3.5" /> {p.short}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${active ? "opacity-90" : "text-slate-500"}`}>{p.hint}</div>
                </button>
              );
            })}
          </div>

          <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-4 block">Not / İhtiyaç *</label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Örn: 16 BGY 277 sağ ön amortisör titreşim yapıyor, yarına kadar takım gerekli."
            rows={3}
            className="mt-1.5 border-slate-300"
            data-testid="op-notes-content-input"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => { setShowForm(false); setContent(""); setPriority("medium"); }}>
              Vazgeç
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !content.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="op-notes-save-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Kaydet
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`op-notes-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border ${
              filter === f.id
                ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-[11px] text-slate-500 font-mono">
          {summary?.open_total ?? 0} açık · {summary?.total ?? 0} toplam
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto" data-testid="op-notes-list">
        {loading && items.length === 0 && (
          <div className="px-5 py-10 text-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Yükleniyor...
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-slate-400 text-sm">
            {filter === "urgent" ? "🎉 Acil not/ihtiyaç yok" : filter === "pending" ? "Bekleyen not yok" : "Henüz kayıt yok"}
          </div>
        )}
        {filtered.map((n) => {
          const pm = P_META[n.priority] || P_META.medium;
          const sm = S_META[n.status] || S_META.open;
          const busy = busyIds.has(n.id);
          const isResolved = n.status === "resolved";
          return (
            <div
              key={n.id}
              data-testid={`op-note-${n.id}`}
              className={`px-5 py-3 flex gap-3 items-start transition ${pm.card} ${pm.border} ${
                n.priority === "critical" && !isResolved ? pm.ring : ""
              } ${isResolved ? "opacity-70" : ""}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pm.badge} ${
                      n.priority === "critical" && !isResolved ? "animate-pulse" : ""
                    }`}
                    data-testid={`op-note-priority-${n.id}`}
                  >
                    <pm.icon className="w-3 h-3" /> {pm.short}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold ${sm.chip}`}
                    data-testid={`op-note-status-${n.id}`}
                  >
                    <sm.icon className="w-3 h-3" /> {sm.label}
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono ml-auto">
                    {n.created_by_name || "—"} · {fmtDate(n.created_at)}
                  </span>
                </div>
                <div className={`mt-1.5 text-sm text-slate-800 whitespace-pre-wrap ${isResolved ? "line-through" : ""}`}>
                  {n.content}
                </div>

                {/* Status quick-toggles */}
                {!isResolved && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {STATUSES.filter((s) => s.id !== n.status).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        disabled={busy}
                        onClick={() => changeStatus(n.id, s.id)}
                        data-testid={`op-note-set-${s.id}-${n.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold transition disabled:opacity-50 ${s.btn}`}
                        title={`Durumu "${s.label}" yap`}
                      >
                        <s.icon className="w-3 h-3" /> {s.label}
                      </button>
                    ))}
                  </div>
                )}
                {isResolved && n.resolved_by_name && (
                  <div className="mt-1.5 text-[10px] text-emerald-700 font-mono">
                    ✓ {n.resolved_by_name} tarafından kapatıldı · {fmtDate(n.resolved_at)}
                    <button
                      onClick={() => changeStatus(n.id, "open")}
                      className="ml-2 underline hover:no-underline"
                      disabled={busy}
                      data-testid={`op-note-reopen-${n.id}`}
                    >Yeniden aç</button>
                  </div>
                )}
              </div>

              {/* Right controls */}
              <div className="flex flex-col gap-1 items-end shrink-0">
                {!isResolved && (
                  <button
                    onClick={() => changeStatus(n.id, "resolved")}
                    disabled={busy}
                    data-testid={`op-note-resolve-${n.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold disabled:opacity-50"
                    title="Çözüldü / Kapat"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Çözüldü
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => remove(n.id)}
                    disabled={busy}
                    data-testid={`op-note-delete-${n.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 text-[11px] disabled:opacity-50"
                    title="Sil"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
                {/* Priority quick change */}
                <div className="hidden md:flex items-center gap-1 mt-1">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => changePriority(n.id, p.id)}
                      disabled={busy || n.priority === p.id}
                      title={p.label}
                      className={`w-3 h-3 rounded-full ${p.dot} border transition ${
                        n.priority === p.id ? "ring-2 ring-offset-1 ring-slate-500" : "opacity-40 hover:opacity-100"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
